// src/data/loadConjugations.js
import Papa from "papaparse";
import { PERSON_SLOTS } from "./languages";
import { loadTranslations } from "./loadTranslations";
import { loadConcepts } from "./loadConcepts";
import { getTenseSchedule } from "./tenseSchedule";
import { expandWordForms } from "./wordForms";
import { normalizeToken, tokenizeTarget } from "../utils/sentenceClozeHelpers";

// unpersönliche Formen, die in den Konjugationsdateien fehlen (dort steht "ha")
const IMPERSONAL_FORMS = { "es-v-0004": "hay" };

/**
 * Lädt die Konjugationen einer Sprache aus public/data/conjugations/<tense>/<langCode>.csv
 * (id, 1s, 2s, 3s, 1p, 2p, 3p – ohne Kopfzeile, id = Concept-ID des Verbs)
 * und gibt sie als Map(id -> { "1s": ..., ..., "3p": ... }) zurück.
 */
export async function loadConjugations(langCode, tense = "present") {
  const url = `/data/conjugations/${tense}/${langCode}.csv`;
  const response = await fetch(url);
  if (!response.ok) {
    console.error(
      `[Fluent] Konnte ${url} nicht laden:`,
      response.status,
      response.statusText
    );
    return new Map();
  }

  const text = await response.text();
  const parsed = Papa.parse(text, { header: false, skipEmptyLines: true });

  const map = new Map();
  for (const cols of parsed.data || []) {
    if (!cols || cols.length < PERSON_SLOTS.length + 1) continue;
    const id = cols[0]?.trim();
    if (!id) continue;
    const forms = {};
    PERSON_SLOTS.forEach((slot, i) => {
      forms[slot] = (cols[i + 1] || "").trim();
    });
    map.set(id, forms);
  }

  console.log(`[Fluent] Konjugationen (${langCode}, ${tense}) geladen:`, map.size);
  return map;
}

/**
 * Verbformen für die Lückentexte: je Verb der Infinitiv (aus translations/<lang>.csv) und
 * die sechs Präsensformen als einzelne Wörter (reflexiv "me quejo" → "quejo").
 * Formen, die zugleich Funktionswörter der Vokabeln sind ("como", "para", "nada" …),
 * werden weggelassen, damit sie im Satz nicht als Verb gelten.
 * Feste Wendungen ("doy a luz", "pido prestado") liefern das gebeugte Verb ("doy", "pido").
 * `otherForms`: die Formen aller anderen Zeitformen (fui, comía, hecho …). Sie zählen nur bei
 * den bekannten Wörtern mit (knownWordsBuilder.js), nicht als Verb im Lückentext.
 * `ambiguous`: Präsensformen, die zugleich Substantiv/Adjektiv/Adverb sind ("casa", "frío",
 * "trabajo"); im Satz nur Verb, wenn sonst keins da ist (analyzeTokens).
 * `impersonal`: unpersönliches "hay" bei haber (steht nicht in den Konjugationsdateien).
 * Französisch: Formen wie im Satz zerlegt (tokenizeTarget), "m'amuse" → "amuse", "souviens-toi"
 * → "souviens"; mehrdeutig sind auch Mehrzahl- und Femininformen ("les portes" / "tu portes");
 * Partizipien in `otherForms` auch angeglichen (allé → allée, allées, allés).
 * `bareInfinitive`: der Infinitiv als einzelnes Wort, wie er im Satz steht ("se souvenir" →
 * "souvenir", "avoir besoin de" → "avoir", "voler (dans le ciel)" → "voler").
 * Rückgabe: [{ id, infinitive, "1s": ..., ..., "3p": ..., otherForms: [...], ambiguous: [...] }]
 */
export async function loadVerbFormsForCloze(langCode) {
  // Zeitformen der Lernsprache (Französisch ohne preterite/subjunctive_imperfect)
  const otherDirs = getTenseSchedule(langCode).map((t) => t.tense).filter((dir) => dir !== "present");
  const [conjugations, translations, concepts, ...otherTenses] = await Promise.all([
    loadConjugations(langCode, "present"),
    loadTranslations(langCode),
    loadConcepts(),
    ...otherDirs.map((dir) => loadConjugations(langCode, dir)),
  ]);

  const functionWords = new Set(
    concepts
      .filter((c) => c.type === "other")
      .map((c) => (translations.get(c.id) || "").toLowerCase())
      .filter(Boolean)
  );

  // Wörter anderer Wortarten; Adjektive stehen als "frío/a" in den Daten, beide Formen zählen
  const otherVocab = new Set();
  for (const c of concepts) {
    if (!["substantiv", "adjektiv", "adverb"].includes(c.type)) continue;
    if (langCode === "fr") {
      // "l'eau (f)" → l'eau, eau, eaux; "petit/e" → petit, petite, petits, petites
      const text = (translations.get(c.id) || "").toLowerCase().replace(/\([^)]*\)/g, " ");
      for (const w of text.split(/\s+/).filter(Boolean)) {
        expandWordForms(w, c.type, "fr").forEach((f) => otherVocab.add(f));
      }
      continue;
    }
    for (const w of (translations.get(c.id) || "").toLowerCase().split(" ")) {
      const [base, suffix] = w.split("/");
      if (!base) continue;
      otherVocab.add(base);
      if (suffix) otherVocab.add(base.endsWith("o") ? base.slice(0, -1) + suffix : base + suffix);
    }
  }

  const verbs = [];
  for (const [id, forms] of conjugations) {
    const infinitive = translations.get(id) || "";
    // "dar a luz" → Ergänzung "a luz", die bei jeder Form hinten abgeschnitten wird
    const complement = infinitive.split(" ").slice(1).join(" ");
    const verbWord = (raw) => {
      let text = raw || "";
      if (complement && text.endsWith(" " + complement)) text = text.slice(0, -complement.length - 1);
      const word = text.split(" ").pop();
      if (langCode !== "fr" || word === "-") return word;
      // letzter Teil, der kein verkürztes oder angehängtes Pronomen ist
      return tokenizeTarget(word, "fr").filter((p) => !/'$|^-/.test(p)).pop() || word;
    };
    const verb = { id, infinitive };
    const ambiguous = [];
    if (langCode === "fr") {
      const plain = infinitive.replace(/\([^)]*\)/g, " ").split("/")[0];
      const bare = tokenizeTarget(plain, "fr").find((p) => !/'$|^-/.test(p) && p !== "se");
      if (bare && bare !== infinitive) {
        verb.bareInfinitive = bare;
        if (otherVocab.has(normalizeToken(bare))) ambiguous.push(bare);
      }
    }
    for (const slot of PERSON_SLOTS) {
      const word = verbWord(forms[slot]);
      if (!word || functionWords.has(word.toLowerCase())) continue;
      verb[slot] = word;
      if (otherVocab.has(langCode === "fr" ? normalizeToken(word) : word.toLowerCase())) ambiguous.push(word);
    }
    if (IMPERSONAL_FORMS[id]) verb.impersonal = IMPERSONAL_FORMS[id];
    const otherForms = new Set();
    otherTenses.forEach((tense, t) => {
      const tenseForms = tense.get(id) || {};
      for (const slot of PERSON_SLOTS) {
        // "he comido" → "comido" (das "he" kommt über haber), "-" = Form gibt es nicht
        const word = verbWord(tenseForms[slot]);
        if (word && word !== "-" && !functionWords.has(word.toLowerCase())) {
          otherForms.add(word);
          // französische Partizipien auch angeglichen (elle est allée, ils sont partis)
          if (langCode === "fr" && otherDirs[t] === "perfect") {
            [word + "e", word + "es", ...(word.endsWith("s") ? [] : [word + "s"])].forEach((f) => otherForms.add(f));
          }
        }
      }
    });
    verb.otherForms = [...otherForms];
    verb.ambiguous = ambiguous;
    verbs.push(verb);
  }
  return verbs;
}
